import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { browserSupportStorage } from 'flow/utils/browser-storage-utils';
import wrappedLocalStorage from 'flow/utils/local-storage-utils';
import ContentWrapper from 'flow/route-components/ContentWrapper';
import TicketOverviewNavTitle from './TicketHeader/TicketOverviewNavTitle';
import ConversationTabContent from './ticket-page/tabs/ConversationTabContent';
import TabsTicket from './ticket-page/TabsTicket';
import TicketSideTabContent from './ticket-page/TicketSideTabContent';
import TicketDetailsDrawer, { DRAWER_WIDTH } from './ticket-page/TicketDetailsDrawer';

// The narrowest main pane the conversation still works in. The feed column
// is 600px wide with 32px of side padding, and the composer footer (tabs,
// actions, send) does not wrap, so 560 keeps it on one line with a small
// margin. The whole ticket row (main pane + panel) is measured, not the main
// pane alone: measuring the pane would make opening the panel shrink it
// below the limit and close the panel again. Below the threshold the panel
// and its toggle are hidden and the conversation gets the whole row.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var MIN_MAIN_PANE_WIDTH = 560;
var DRAWER_WIDTH_THRESHOLD = MIN_MAIN_PANE_WIDTH + DRAWER_WIDTH; // 920
var PREFERS_DRAWER_OPEN_KEY = 'prefersTicketDetailsOpen';
var MAIN_RELATED_SCROLL_ID = 'ticket-main-related-scroll';
var readPrefersDrawerOpen = function readPrefersDrawerOpen() {
  try {
    return browserSupportStorage() && wrappedLocalStorage.getItem(PREFERS_DRAWER_OPEN_KEY) === 'true';
  } catch (_unused) {
    return false;
  }
};
var Ticket = function Ticket() {
  var _useParams = useParams(),
    ticketKey = _useParams.ticketKey;
  var containerRef = useRef(null);
  var dockToggleRef = useRef(null);
  // Only the threshold crossing matters, so the state is the comparison,
  // not the width: a window drag or the panel animation then re-renders the
  // ticket once, not on every pixel. `undefined` until the first measurement.
  var _useState = useState(undefined),
    _useState2 = _slicedToArray(_useState, 2),
    wideEnough = _useState2[0],
    setWideEnough = _useState2[1];
  var _useState3 = useState(readPrefersDrawerOpen),
    _useState4 = _slicedToArray(_useState3, 2),
    prefersDrawerOpen = _useState4[0],
    setPrefersDrawerOpen = _useState4[1];
  var _useState5 = useState('CONVERSATION'),
    _useState6 = _slicedToArray(_useState5, 2),
    selectedTab = _useState6[0],
    setSelectedTab = _useState6[1];
  // Open/close is only animated once the pane has been measured and painted
  // in its settled state — see the `animate` effect below.
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    animate = _useState8[0],
    setAnimate = _useState8[1];

  // Reset to the conversation when switching tickets. A layout effect so
  // the previous ticket's tab is never painted on the next ticket.
  useLayoutEffect(function () {
    setSelectedTab('CONVERSATION');
  }, [ticketKey]);
  useLayoutEffect(function () {
    // useLayoutEffect (not useEffect) to avoid a panel flicker on mount.
    var el = containerRef.current;
    if (!el) return undefined;
    var update = function update() {
      return setWideEnough(el.offsetWidth >= DRAWER_WIDTH_THRESHOLD);
    };
    var observer = new ResizeObserver(update);
    observer.observe(el);
    update();
    return function () {
      return observer.disconnect();
    };
  }, []);

  // Until the pane has been measured its width is unknown — treat that as
  // "not wide enough" rather than assuming wide. Assuming wide meant a saved
  // open preference rendered the panel open on the first frame, so a narrow
  // pane then animated it *closed* on every navigation.
  var measured = wideEnough !== undefined;
  var drawerOpen = prefersDrawerOpen && !!wideEnough;

  // However the panel closes (its close button, or the row narrowing below
  // the threshold), the main pane returns to the conversation: a side tab
  // picked inside the panel must not replace the conversation and composer.
  // The saved preference is kept, so re-widening docks the panel again on
  // its Ticket details fallback. A layout effect so the side tab is never
  // painted in the main pane.
  var drawerWasOpenRef = useRef(drawerOpen);
  useLayoutEffect(function () {
    if (drawerWasOpenRef.current && !drawerOpen) {
      setSelectedTab('CONVERSATION');
    }
    drawerWasOpenRef.current = drawerOpen;
  }, [drawerOpen]);

  // Enable transitions only after a frame has been painted in the measured
  // state, so the initial settle is instant and only later open/close
  // (toggle, or a resize across the threshold) animates.
  useEffect(function () {
    if (!measured) return undefined;
    var frameId = requestAnimationFrame(function () {
      return setAnimate(true);
    });
    return function () {
      return cancelAnimationFrame(frameId);
    };
  }, [measured]);
  var setDrawerOpen = useCallback(function (value) {
    setPrefersDrawerOpen(value);
    if (browserSupportStorage()) {
      wrappedLocalStorage.setItem(PREFERS_DRAWER_OPEN_KEY, value ? 'true' : 'false');
    }
  }, []);
  var openDrawer = useCallback(function () {
    return setDrawerOpen(true);
  }, [setDrawerOpen]);

  // Closing the panel always returns the main pane to the conversation.
  var closeDrawer = useCallback(function () {
    setSelectedTab('CONVERSATION');
    setDrawerOpen(false);
  }, [setDrawerOpen]);

  // The conversation occupies the main pane whenever the panel is open
  // (pinned there) or the conversation tab is selected while closed.
  var showConversationInMain = drawerOpen || selectedTab === 'CONVERSATION';
  return /*#__PURE__*/_jsx(ContentWrapper, {
    children: /*#__PURE__*/_jsxs("div", {
      ref: containerRef,
      className: "flex w-full grow",
      children: [/*#__PURE__*/_jsxs("div", {
        className: "flex w-full grow flex-col overflow-hidden",
        children: [/*#__PURE__*/_jsx("div", {
          className: "h-16 shrink-0",
          children: /*#__PURE__*/_jsx(TicketOverviewNavTitle, {
            ticketKey: ticketKey
          })
        }), /*#__PURE__*/_jsx("div", _objectSpread(_objectSpread({
          className: twMerge('grid shrink-0', animate ? 'transition-all duration-300 ease-out' : '', drawerOpen ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100')
          // Collapsed rows stay mounted for the animation; keep
          // them out of the tab order and accessibility tree.
          // React 18 passes `inert` through as a plain attribute
          // (`''` = present); React 19 types it as a boolean, so
          // this becomes `inert={drawerOpen}` on upgrade.
        }, drawerOpen ? {
          inert: ''
        } : {}), {}, {
          "aria-hidden": drawerOpen || undefined,
          children: /*#__PURE__*/_jsx("div", {
            className: "min-h-0 overflow-hidden",
            children: /*#__PURE__*/_jsx("div", {
              className: "py-3",
              style: {
                paddingInlineStart: 'max(0px, calc(50% - 316px))'
              },
              children: /*#__PURE__*/_jsx(TabsTicket, {
                ticketKey: ticketKey,
                selectedTab: selectedTab,
                onSelectTab: setSelectedTab,
                showDock: !!wideEnough,
                onToggleDock: openDrawer,
                dockToggleRef: dockToggleRef
              })
            })
          })
        })), /*#__PURE__*/_jsx("div", {
          className: twMerge('flex min-h-0 flex-1 flex-col', showConversationInMain ? '' : 'hidden'),
          children: /*#__PURE__*/_jsx(ConversationTabContent, {
            ticketKey: ticketKey,
            visible: showConversationInMain
          })
        }), !drawerOpen && selectedTab !== 'CONVERSATION' ? /*#__PURE__*/_jsx(TicketSideTabContent, {
          ticketKey: ticketKey,
          tab: selectedTab,
          relatedScrollId: MAIN_RELATED_SCROLL_ID
        }) : null]
      }), measured ? /*#__PURE__*/_jsx(TicketDetailsDrawer, {
        ticketKey: ticketKey,
        selectedTab: selectedTab,
        onSelectTab: setSelectedTab,
        onClose: closeDrawer,
        open: drawerOpen,
        returnFocusTo: dockToggleRef
      }) : null]
    })
  });
};
export default /*#__PURE__*/React.memo(Ticket);